import { makeAutoObservable } from 'mobx';
import { AutomaticWithdrawalState, isForReviewTab } from 'src/AppState/AutomaticWithdrawalState';
import { FormInputState, FormModel } from 'src_common/common/mobx-utils/Form';
import { WithdrawalViewModelType } from 'src/api/config/withdrawal/withdrawalList';
import { assertNever } from 'src_common/common/assertNever';
import { StaffAccountBasicDataType } from 'src/AppState/sdk/staff';
import { ModelsState } from 'src/AppState/Models/ModelsState';

export type ConfirmationType = null | 'reject' | 'approve' | 'review';

interface OnSetRuleType {
    ruleType: 'approve' | 'reject';
    amount: string | null;
    reason: Array<string> | null;
}

const actionTypeBodyPart = (obj: OnSetRuleType): OnSetRuleType | never => {
    const { ruleType, amount, reason } = obj;
    switch (ruleType) {
        case 'reject':
            return {
                ruleType: ruleType,
                amount: null,
                reason: reason,
            };
        case 'approve':
            return {
                ruleType: ruleType,
                amount: amount,
                reason: null,
            };
        default:
            return assertNever('Wrong Rule Type', ruleType);
    }
};

export class WithdrawalPendingRowState {
    public confirmationType: ConfirmationType = null;
    public isHiddenRow: boolean = false;

    public readonly notesTextModel: FormModel<string>;
    public readonly notesText: FormInputState<string>;

    public constructor(
        private readonly automaticWithdrawalState: AutomaticWithdrawalState,
        private readonly withdrawalId: () => number,
        private readonly models: ModelsState
    ) {
        this.automaticWithdrawalState = automaticWithdrawalState;
        this.withdrawalId = withdrawalId;

        const initialNote = this.automaticWithdrawalState.getItem(this.withdrawalId())?.automaticWithdrawal?.note ?? '';
        this.notesText = new FormInputState(initialNote, false);
        this.notesTextModel = this.notesText.toModel();
        makeAutoObservable(this);
    }

    public get withdrawalModel(): WithdrawalViewModelType | null {
        return this.automaticWithdrawalState.getItem(this.withdrawalId());
    }

    public review30Days = async (): Promise<void> => {
        if (this.withdrawalModel === null) {
            return;
        }

        const body = {
            transactionId: this.withdrawalModel.id,
            card: this.withdrawalModel.cardNumber,
            accountId: this.withdrawalModel.accountId,
            note: this.notesText.value,
        };

        this.confirmationType = 'review';
        await this.automaticWithdrawalState.execReview(body);
    };

    public onSetRule = async ({ ruleType, amount, reason }: OnSetRuleType): Promise<void> => {
        if (this.withdrawalModel === null) {
            return;
        }
        const { cardNumber, accountId } = this.withdrawalModel;

        const mutualPart = {
            card: cardNumber,
            accountId: accountId,
            note: this.notesText.value,
        };

        const fullBody = {
            ...mutualPart,
            ...actionTypeBodyPart({ ruleType, amount, reason }),
        };

        this.confirmationType = ruleType;
        await this.automaticWithdrawalState.execSetOrUpdateRule(fullBody);
    };

    public onHide = (): void => {
        this.isHiddenRow = true;
    };

    public get hideButtonOnReviewTab(): boolean {
        const date = this.withdrawalModel?.automaticWithdrawal?.doNotShowUntil ?? false;
        if (date === false) {
            return false;
        }
        return isForReviewTab(date);
    }

    public get basicDataLocal(): StaffAccountBasicDataType | null {
        const accountId = this.withdrawalModel?.accountId;
        if (accountId === undefined) {
            return null;
        }
        return this.models.getUserModel(accountId).basicData.data;
    }
}
